import { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize from localStorage
  const [cart, setCartState] = useState(() => {
    try {
      const saved = localStorage.getItem("360lumina_cart");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if cart is still valid (not expired - 24 hour expiry)
        if (parsed.expiry && Date.now() < parsed.expiry) {
          return parsed.items || [];
        }
      }
    } catch (e) {
      console.error("Failed to load cart from localStorage:", e);
    }
    return [];
  });

  // Track which gallery the cart items belong to
  const [cartGalleryId, setCartGalleryId] = useState(() => {
    try {
      const saved = localStorage.getItem("360lumina_cart");
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.galleryId || null;
      }
    } catch (e) {
      return null;
    }
  });

  // Persist to localStorage whenever cart changes
  useEffect(() => {
    try {
      localStorage.setItem("360lumina_cart", JSON.stringify({
        items: cart,
        galleryId: cartGalleryId,
        expiry: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
      }));
    } catch (e) {
      console.error("Failed to save cart to localStorage:", e);
    }
  }, [cart, cartGalleryId]);

  // Custom setCart that handles gallery switching
  const setCart = (newCart, galleryId = null) => {
    if (typeof newCart === "function") {
      setCartState(prev => newCart(prev));
    } else {
      setCartState(newCart);
    }
    if (galleryId) {
      setCartGalleryId(galleryId);
    }
  };

  // Clear cart when switching galleries
  const switchGallery = (newGalleryId) => {
    if (cartGalleryId && cartGalleryId !== newGalleryId && cart.length > 0) {
      // Cart has items from a different gallery - clear it
      setCartState([]);
    }
    setCartGalleryId(newGalleryId);
  };

  // Add item to cart
  const addToCart = (item, galleryId) => {
    // If switching galleries, clear cart first
    if (cartGalleryId && cartGalleryId !== galleryId) {
      setCartState([item]);
      setCartGalleryId(galleryId);
      return;
    }
    
    // Check if already in cart
    if (cart.find(i => i.asset_id === item.asset_id)) {
      return false; // Already in cart
    }
    
    setCartState(prev => [...prev, item]);
    setCartGalleryId(galleryId);
    return true;
  };

  // Remove item from cart
  const removeFromCart = (assetId) => {
    setCartState(prev => prev.filter(item => item.asset_id !== assetId));
  };

  // Clear cart
  const clearCart = () => {
    setCartState([]);
    setCartGalleryId(null);
  };

  // Get cart total
  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price || 0), 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      setCart,
      cartGalleryId,
      switchGallery,
      addToCart,
      removeFromCart,
      clearCart,
      getCartTotal,
      cartCount: cart.length
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
